import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';
import { formSections, FormField } from '../data/intakeFormSection';
import { useAppContext } from '../context/AppContext';
import { useModal } from '../context/ModalContext';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { api, handleApiError } from '../utils/api';

interface Props {
  session: Session;
}

interface FormData {
  [fieldId: string]: string;
}

interface ExtendedAnalysisData {
  [key: string]: any;
  dynamicFeedback: string;
}

interface JudgeQuestion {
  question_id: string;
  question: string;
  answer: string;
}

interface JudgeResponse {
  reasoning: string;
  acceptable: string;
}

interface UpdateQuestionRequest {
  question_id: string;
  answer: string;
}

const IntakeForm: React.FC<Props> = ({ session }) => {
  const navigate = useNavigate();
  const { analysisData: contextAnalysisData, setAnalysisData } = useAppContext();
  const { isIntakeFormOpen, closeIntakeForm } = useModal();
  const [currentSection, setCurrentSection] = useState(1);
  const [loadingFields, setLoadingFields] = useState<{ [key: string]: boolean }>({});
  const { applicationId } = useAppContext();
  const [savingStatus, setSavingStatus] = useState<{ [key: string]: 'saving' | 'saved' | 'error' | null }>({});
  const { executeRecaptcha } = useRecaptcha();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Initialize form data
  const initialFormData: FormData = {};
  formSections.forEach(section => {
    section.categories.forEach(category => {
      category.subCategories.forEach(subCategory => {
        subCategory.fields.forEach(field => {
          initialFormData[field.id] = '';
        });
      });
    });
  });

  const [formData, setFormData] = useState<FormData>(initialFormData);
  
  // For state management of analysis data with commentaries
  const [analysisData, setLocalAnalysisData] = useState<ExtendedAnalysisData | null>();

  // Add state for loading and error handling
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Load analysis data from localStorage if not available in context
  useEffect(() => {
    if (!isIntakeFormOpen || !applicationId) return;

    // Check context first (most up-to-date) - ALWAYS prioritize context over local state
    if (contextAnalysisData) {
      setLocalAnalysisData(contextAnalysisData);
      // Save to localStorage with applicationId prefix for persistence
      localStorage.setItem(`analysisData_${applicationId}`, JSON.stringify(contextAnalysisData));
      setLoadError(null);
      return;
    }

    // Only check local state if context is empty
    if (analysisData) {
      return;
    }

    // Fall back to localStorage if context is empty (but only for current applicationId)
    const storedData = localStorage.getItem(`analysisData_${applicationId}`);
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        setLocalAnalysisData(parsedData);
        setAnalysisData(parsedData); // Also update context
        setLoadError(null);
      } catch (error) {
        console.error('Error parsing stored analysis data:', error);
        localStorage.removeItem(`analysisData_${applicationId}`); // Remove corrupted data
      }
    } else {
      console.log('IntakeForm: No data found in localStorage or context');
    }
  }, [isIntakeFormOpen, contextAnalysisData, analysisData, setAnalysisData, applicationId]);

  // Populate form data with analysis data when component mounts or analysis data changes
  useEffect(() => {

    // Use whichever data source we have (prioritize local, then context)
    const dataToUse = analysisData || contextAnalysisData;
    
    if (dataToUse) {
      const populatedFormData: FormData = { ...initialFormData };
      
      // Map analysis data to form fields
      let mappedFieldsCount = 0;
      formSections.forEach(section => {
        section.categories.forEach(category => {
          category.subCategories.forEach(subCategory => {
            subCategory.fields.forEach(field => {
              const fieldId = field.id;
              if (fieldId in dataToUse) {
                const value = (dataToUse as any)[fieldId];
                if (value !== null && value !== undefined && value !== '') {
                  populatedFormData[field.id] = value;
                  mappedFieldsCount++;
                }
              }
            });
          });
        });
      });
      setFormData(populatedFormData);
    }
  }, [analysisData, contextAnalysisData]); // Added contextAnalysisData as dependency

  // Add auto-save function
  const autoSaveField = async (fieldId: string, value: string) => {
    if (!applicationId) {
      console.error('No application ID available for auto-save');
      setSavingStatus(prev => ({ ...prev, [fieldId]: 'error' }));
      return;
    }

    try {
      const updateRequest: UpdateQuestionRequest = {
        question_id: fieldId,
        answer: value
      };

      await api.updateApplication(applicationId, updateRequest);

      setSavingStatus(prev => ({ ...prev, [fieldId]: 'saved' }));
      setTimeout(() => {
        setSavingStatus(prev => ({ ...prev, [fieldId]: null }));
      }, 2000);
    } catch (error) {
      console.error('Failed to auto-save field:', fieldId, handleApiError(error));
      setSavingStatus(prev => ({ ...prev, [fieldId]: 'error' }));
    }
  };

  // Modify handleInputChange to only update local state immediately
  const handleInputChange = (fieldId: string, value: string) => {
    // Update local form state immediately - this is the only state update that affects the input
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  // Add new handler for blur events
  const handleBlur = (fieldId: string) => {
    const value = formData[fieldId];
    
    // Update analysis data when user finishes editing
    setAnalysisData(prevData => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        [fieldId]: value
      };
    });
    
    // Auto-save the field
    autoSaveField(fieldId, value);
  };

  const handleJudgeQuestion = async (fieldId: string, questionLabel: string, questionId: string, applicationId: string) => {
    // Prevent multiple simultaneous calls for the same field
    if (loadingFields[fieldId]) {
      return;
    }

    try {
      setLoadingFields(prev => ({ ...prev, [fieldId]: true }));
      
      const judgeQuestion: JudgeQuestion = {
        question_id: questionId,
        question: questionLabel,
        answer: formData[fieldId]
      };

      const result = await api.judgeQuestion(applicationId, judgeQuestion);

      // Update both local state and context state while preserving the original input
      setLocalAnalysisData(prevData => {
        if (!prevData) return prevData;
        const updatedData = {
          ...prevData,
          [fieldId]: formData[fieldId], // Preserve the original input
          [`${fieldId}_commentary`]: result.reasoning,
          [`${fieldId}_score`]: result.acceptable
        };
        return updatedData;
      });

      setAnalysisData(prevData => {
        if (!prevData) return prevData;
        const updatedData = {
          ...prevData,
          [fieldId]: formData[fieldId], // Preserve the original input
          [`${fieldId}_commentary`]: result.reasoning,
          [`${fieldId}_score`]: result.acceptable
        };
        return updatedData;
      });

    } catch (error) {
      console.error('Error judging question:', handleApiError(error));
    } finally {
      setLoadingFields(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  const renderSavingStatus = (fieldId: string) => {
    const status = savingStatus[fieldId];
    
    if (!status) return null;

    const statusConfig = {
      saving: {
        icon: (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ),
        text: 'Saving...',
        color: 'text-gray-500'
      },
      saved: {
        icon: (
          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="12" r="8" />
          </svg>
        ),
        text: 'Saved',
        color: 'text-green-500'
      },
      error: {
        icon: (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ),
        text: 'Error saving',
        color: 'text-red-500'
      }
    };

    const config = statusConfig[status];
    
    return (
      <div className={`absolute bottom-2 right-2 flex items-center gap-1 text-xs ${config.color}`}>
        {config.icon}
        <span>{config.text}</span>
      </div>
    );
  };

  const renderField = (field: FormField) => {
    const fieldValue = formData[field.id] || '';
    const commonProps = {
      value: fieldValue,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleInputChange(field.id, e.target.value);
      },
      onBlur: () => handleBlur(field.id),
      className: "w-full p-2 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-[#57547B] focus:border-transparent transition-all duration-200",
      placeholder: `Enter ${field.label?.toLowerCase() || 'your response'}...`
    };

    const fieldId = field.id;
    const commentary = analysisData?.[`${fieldId}_commentary`];
    const score = analysisData?.[`${fieldId}_score`];

    const renderScoreIndicator = (score: string) => {
      if (score.toLowerCase() === 'pass') {
        return (
          <div className="flex flex-col items-center gap-1 text-green-500">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-medium">Great!</span>
          </div>
        );
      } else {
        return (
          <div className="flex flex-col items-center gap-1 text-yellow-500">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-sm font-medium">Review</span>
          </div>
        );
      }
    };

    return (
      <div className="space-y-2">
        <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
          <div className={`${field.id != 'company_name' ? 'flex justify-between items-center mb-3' : ''}`}>
            <label className="block text-black font-semibold">{field.label}</label>
            {score && (
              <div className="shrink-0">
                {renderScoreIndicator(score)}
              </div>
            )}
          </div>
          <div className="flex gap-2 relative">
            {field.type === 'textarea' ? (
              <textarea
                {...commonProps}
                rows={field.rows || 4}
                className={`${commonProps.className} resize-y`}
              />
            ) : (
              <input
                type="text"
                {...commonProps}
              />
            )}
            {renderSavingStatus(field.id)}
          </div>
          
          {(commentary || field.showRefreshButton) && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex flex-row items-start gap-4 w-full">
                {commentary && (
                  <div className="text-sm text-black flex-1 bg-[#F1F1F1] p-3 rounded">
                    {field.showRefreshButton && (
                      <div className="flex flex-row items-center gap-2">
                        <p className="text-sm text-[#898989]">AI Feedback</p>
                        <button
                          onClick={() => handleJudgeQuestion(field.id, field.label || '', field.id || '', applicationId || '')}
                          className="px-3 py-1 bg-[#57547B] text-white rounded hover:bg-[#4a4866] flex items-center justify-center text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Refresh analysis"
                          disabled={loadingFields[field.id]}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className={`h-4 w-4 ${loadingFields[field.id] ? 'animate-spin' : ''}`}
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={2} 
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                    {commentary}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSection = () => {
    const currentSectionData = formSections[currentSection - 1];
    
    return (
      <div className="space-y-8">
        <h2 className="text-2xl font-bold text-black mb-6">{currentSectionData.title}</h2>
        
        {currentSection === 1 && (
          <p className="text-black mb-6">
            Tell us about your idea, the progress you've made so far, and how you see it aligning with AXL's mission. We're
            looking for focused, high-impact opportunities that fit our venture studio model—where we can collaborate to build and scale
            together.
          </p>
        )}
        
        {currentSectionData.categories.map(category => (
          <div key={category.id} className="space-y-6">
            <h3 className="text-xl font-bold text-black">{category.title}</h3>
            
            {category.subCategories.map(subCategory => (
              <div key={subCategory.id} className="space-y-4">
                <h4 className="text-lg font-semibold text-black">{subCategory.title}</h4>
                
                {subCategory.fields.map(field => (
                  <div key={field.id} className="space-y-2">
                    {renderField(field)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const handleSubmit = async () => {
    if (!applicationId || isSubmitting) {
      console.error('No application ID available for form submission or already submitting');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Execute reCAPTCHA before form submission
      const recaptchaToken = await executeRecaptcha('SUBMIT_FORM');
      
      if (!recaptchaToken) {
        console.error('reCAPTCHA verification failed');
        return;
      }

      // Data structure for backend submission
      const backendData: { [key: string]: string } = {};
      
      // Collect data
      Object.entries(formData).forEach(([fieldId, value]) => {
        backendData[fieldId] = value;
        if (fieldId !== 'company_name') {
          const commentary = analysisData?.[`${fieldId}_commentary`];
          backendData[`${fieldId}_commentary`] = commentary || "";

          const score = analysisData?.[`${fieldId}_score`];
          backendData[`${fieldId}_score`] = score || "";
        }
      });

      backendData["dynamic_feedback"] = analysisData?.dynamicFeedback || "";

      // Save data structure to localStorage
      localStorage.setItem('formData', JSON.stringify(backendData));

      // Submit to backend API with reCAPTCHA token
      await api.submitForm(applicationId, recaptchaToken);
      
      // Close modal and navigate to confirmation page upon successful submission
      closeIntakeForm();
      navigate('/confirmation');
    } catch (error) {
      console.error('Error submitting form:', handleApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextSection = () => {
    if (currentSection < formSections.length) {
      setCurrentSection(currentSection + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevSection = () => {
    if (currentSection > 1) {
      setCurrentSection(currentSection - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSectionClick = (sectionNumber: number) => {
    if (sectionNumber >= 1 && sectionNumber <= formSections.length) {
      setCurrentSection(sectionNumber);
      window.scrollTo(0, 0);
    }
  };

  // Manual retry function
  const handleManualRetry = async () => {
    if (!applicationId) return;
    
    setIsLoadingData(true);
    setLoadError(null);
    
    try {
      const intakeFormData = await api.getIntakeForm(applicationId);
      if (intakeFormData.intake_form) {
        setLocalAnalysisData(intakeFormData.intake_form);
        setAnalysisData(intakeFormData.intake_form);
        localStorage.setItem(`analysisData_${applicationId}`, JSON.stringify(intakeFormData.intake_form));
      } else {
        setLoadError('Analysis data is still being generated. Please wait a few more minutes.');
      }
    } catch (error) {
      console.error('Error manually loading analysis data:', error);
      setLoadError('Failed to load analysis data. Please try again.');
    } finally {
      setIsLoadingData(false);
    }
  };

  if (!isIntakeFormOpen) return null;

  if (!analysisData) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeIntakeForm();
          }
        }}
      >
        <div 
          className="bg-white p-10 rounded-xl shadow-2xl max-w-2xl text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col items-center gap-4">
            {isLoadingData ? (
              <svg className="animate-spin h-12 w-12 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="animate-spin h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            
            <h2 className="text-2xl font-bold text-black mb-2">
              {isLoadingData ? 'Refreshing...' : 'Preparing Your Intake Form'}
            </h2>
            
            {loadError ? (
              <div className="text-center">
                <p className="text-red-600 mb-4">{loadError}</p>
                <button
                  onClick={handleManualRetry}
                  disabled={isLoadingData}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-gray-600 leading-relaxed mb-4">
                  Our AI is analyzing your pitch deck and generating personalized questions. 
                  This process typically takes 2-5 minutes.
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Please wait while we prepare your customized application form...
                </p>
                <button
                  onClick={handleManualRetry}
                  disabled={isLoadingData}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isLoadingData ? 'Checking...' : 'Check if Ready'}
                </button>
              </div>
            )}
          </div>
          
          <div className="mt-8 flex justify-center">
            <button
              onClick={closeIntakeForm}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Close and Return Later
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeIntakeForm();
        }
      }}
    >
      <div 
        className="bg-white p-8 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative scrollbar-hide"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-8 border-b border-gray-200 pb-4">
          <div>
            <h2 className="text-3xl font-bold text-black mb-2">AXL Intake Form</h2>
            <p className="text-black"></p>
          </div>
          <button
            onClick={closeIntakeForm}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress indicator */}
        <div className="mb-8">
          <div className="flex justify-between">
            {formSections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => handleSectionClick(index + 1)}
                className={`text-sm font-medium transition-colors duration-200 ${
                  currentSection >= index + 1 
                    ? 'text-[#57547B] hover:text-[#4a4866]' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {section.title}
              </button>
            ))}
          </div>
          <div className="mt-2 h-2 bg-gray-200 rounded-full">
            <div 
              className="h-2 bg-[#57547B] rounded-full transition-all duration-300"
              style={{ width: `${(currentSection / formSections.length) * 100}%` }}
            />
          </div>
        </div>
        
        {/* Form content */}
        {renderSection()}
        
        {/* Navigation buttons */}
        <div className="mt-10 flex justify-between">
          {currentSection > 1 ? (
            <button 
              onClick={prevSection}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Previous
            </button>
          ) : (
            <div /> 
          )}
          
          {currentSection < formSections.length ? (
            <button 
              onClick={nextSection}
              className="px-6 py-2 bg-[#57547B] text-white rounded hover:bg-[#4a4866]"
            >
              Next
            </button>
          ) : (
            <button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting && (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntakeForm; 